// Verbatim from docs/spec/modules/settings.md and its Live-verified section (including Clockify's typos).
export const s = {
  sections: {
    general: "General",
    timeTracking: "Time tracking",
    finances: "Finances",
    timePlanning: "Time planning",
    userActivity: "User activity",
    kiosk: "Kiosk",
    security: "Security",
    accounts: "Accounts",
    permissions: "Permissions",
    integrations: "Integrations & add-ons",
    import: "Import/Export",
  },
  updated: "Workspace settings updated",
  updating: "Updating...",
  failed: "Something went wrong. Please try again later.",
  edit: "Edit",
  cancel: "Cancel",
  save: "Save",
  create: "Create",
  delete: "Delete",
  none: "None",
  info: "More information",

  general: {
    wsInfo: "Workspace info",
    wsName: "Workspace name",
    editInfoTitle: "Edit workspace info",
    namePlaceholder: "Enter Workspace name",
    nameError: "The name must contain 1-50 characters. Links or special formatting is not allowed.",
    required: "This field is required.",
    infoHelp: "Your workspace logo and name will appear on shared reports, PDF exports, and invoices.",
    logoCaption: "Formats: png, jpg. Max size: 5 MB.",
    workweek: "Workweek",
    weekStart: "Week start",
    weekStartDesc: "Default week start for all new members. You can override week start on member's profile.",
    workdays: "Workdays",
    workdaysDesc: "Default workdays for all new members. You can override workdays on member's profile.",
    oneWorkday: "Must have at least one working day",
    capacity: "Daily work capacity",
    capacityDesc:
      "Default daily work capacity for all new members. You can override work capacity on member’s profile.",
    capacityValue: (h: number, m: number) =>
      m === 0 ? `${h} ${h === 1 ? "hour" : "hours"}` : `${h} ${h === 1 ? "hour" : "hours"} ${m} min`,
    capacityInput: "Default daily capacity",
    hours: "Hours",
    minutes: "Minutes",
    applyTo: "Apply changes to:",
    applyDefault: "New and existing users currently using default",
    applyDefaultTip: "Doesn't override custom capacities",
    applyAll: "All users",
    applyAllTip: "Overrides custom capacities",
    overtime: "Overtime calculation period",
    overtimeDesc:
      "Select the period to calculate overtime. This period defines the grouping and filters applied to the attendance report.",
    overtimeTip:
      "The system calculates total capacity for the selected period (Working Days × Daily Capacity). Any tracked time above that is overtime.",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    projectPrefs: "Project preferences",
    defaultBillable: (p: string) => `Default billable ${p}s`,
    defaultBillableDesc: (p: string) =>
      `All new ${p}s will be billable by default, including their time entries. You can override billability in ${p} settings.`,
    defaultPublic: (p: string) => `Default public ${p}s`,
    defaultPublicDesc: (p: string) =>
      `All new ${p}s will be public by default, giving everyone access to track time on them. You can override visibility in ${p} settings.`,
    billableOff: "This permission is disabled because Billable hours isn’t activated.",
  },
  weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  weekdayLetters: ["M", "T", "W", "T", "F", "S", "S"],

  tracking: {
    trackingApproval: "Tracking & Approval",
    timesheet: "Timesheet view",
    timesheetDesc: (p: string) => `Track time in the Timesheet view (${p} is a required field for each entry).`,
    hideTracker: "Hide time tracker view",
    breaks: "Breaks",
    breaksDesc: "Track breaks and label them for more accurate analysis and reports.",
    defaultBreak: (p: string) => `Default break ${p}`,
    approval: "Time approval",
    approvalDesc: "All users must submit timesheets for review and approval.",
    approvalConfig: "Time approval configuration",
    approvalConfigDesc: "Configure the submission frequency for your timesheet workflow.",
    periodTip: "Choose from the available options: weekly, semi-monthly (1-15 and 16-end of month) and monthly",
    semiMonthly: "Semi-monthly",
    entryFormat: "Time entry format",
    duration: "Duration format",
    durationDesc: "Choose how to display tracked time.",
    full: "Full (hh:mm:ss)",
    compact: "Compact (h:mm)",
    decimal: "Decimal (h.hh)",
    roundDecimal: "Round duration to 2 decimal places",
    compactOn: "All entries are round up to 1 minute while the compact mode (h:mm) is selected.",
    compactOff: "Time entries are no longer rounded up.",
    labels: "Customize labels",
    labelsDesc: "Customize default time entry labels and organizational hierarchy.",
    labelsTitle: "Organize time by",
    labelsIntro: "Choose how you wish to group time, and set up custom terms and hierarchy.",
    groupBy: "Group projects by",
    groupHint: "If you don’t have clients or departments, you can change the label to something else.",
    client: "Client",
    department: "Department",
    category: "Category",
    custom: "Custom...",
    projectLabel: "Project label",
    taskLabel: "Task label",
    cfTitle: "Time entry custom fields",
    cfDesc: "Add details to time entries for more granular reports and analysis with custom fields.",
    manageCf: "Manage custom fields",
    required: "Time entry required fields",
    requiredDesc: "Select the required fields for saving a time entry.",
    tag: "Tag",
    description: "Description",
    projectForced: "To disable required project selection, timesheet must be off",
    requiredBlocked: "Can\u2019t require this field. Either set its default value or allow everyone to edit it.",
    rounding: "Round time in reports",
    roundingDesc: "Choose how to round time in reports.",
    setRounding: "Set rounding",
    roundingTitle: "Time rounding settings",
    roundUp: "Round up to",
    roundUpEx: "e.g. if you set the interval to 30min, a 26min entry will show up in reports as 30min.",
    roundNearest: "Round to nearest",
    roundNearestEx:
      "e.g. if you set the interval to 30min, a 14min entry will show up as 0min, and 26min entry as 30min.",
    roundDown: "Round down to",
    roundDownEx: "e.g. if you set the interval to 30min, a 26min entry will show up in reports as 0min.",
    roundSummary: (mode: string, min: string) => `${mode} ${min} minutes`,
    interval: "Interval",
    roundOnByDefault: "Rounding is on by default in reports",
    display: "Time data display",
    displayDesc:
      "Choose how time entries are displayed across different time zones. The Original time zone keeps data uniform for all users, ensuring everyone sees the same times.",
    original: "Original time zone",
    originalLabel: "Display Original Time Zone",
    originalTip:
      "All time entries are displayed as they were originally entered in the system, without a time zone conversion.",
    viewer: "Viewer time zone",
    viewerLabel: "Display Viewer Time Zone",
    viewerTip: "All time entries are translated to the time zone set by the viewer.",
    originalOn: "Original Time View is now active on this workspace",
    viewerOn: "Local Time View is now active on this workspace",
    rules: "Tracking rules",
    lock: "Lock time and expenses",
    lockDesc: "Prevent members from adding or editing time and expenses for past dates.",
    notLocked: "Not locked",
    lockedBefore: (d: string) => `Locked before ${d}`,
    lockType: "Type",
    dontLock: "Don't lock time and expenses",
    fixedLock: "Fixed lock date",
    autoLock: "Automatically update lock date",
    lockBefore: "Lock time before",
    autoInfo: "Your lock date will get auto-updated each day, week, or month.",
    frequency: "Frequency",
    every: "Every",
    lockPrevWeek: "lock the previous week. First day of the week is",
    on: "On the",
    lockPrevMonth: "of every month, lock the previous month",
    monthlyHint: "For teams in different time zones, lock on the 2nd of the month.",
    olderThan: "Lock entries older than",
    at: "at",
    days: "days",
    weeks: "weeks",
    months: "months",
    autoWeekly: (day: string) => `Every ${day} at 00:00 lock the previous week`,
    autoMonthly: (n: number) => `On the ${n} at 00:00 of every month, lock the previous month`,
    autoOlder: (n: number, p: string) => `Lock entries older than ${n} ${p} at 00:00`,
    forceTimer: "Force timer",
    forceTimerDesc: "Prevent users from manually adding time or editing their existing entries.",
    favorites: "Favorites",
    projectFavorites: (p: string, t: string) => `${p} and ${t} favorites`,
    projectFavoritesDesc: (p: string, t: string) => `Mark ${p}s and ${t}s as favorites for quicker access.`,
    favoriteEntries: "Favorite entries",
    favoriteEntriesDesc: "Mark time entries as favorites for quicker access to time tracking details.",
  },

  finances: {
    dataFormat: "Data format",
    currencies: "Currencies",
    currenciesDesc: "Add and manage currencies that will be used in your workspace.",
    defaultCurrency: (c: string) => `Default: ${c}`,
    manageCurrencies: "Manage currencies",
    editCurrencies: "Edit currencies",
    currenciesIntro:
      "Add workspace currencies. The default workspace currency applies unless you override it with a client-specific currency.",
    default: "Default",
    setDefault: "Set as default",
    removeCurrency: "Remove currency",
    addCurrency: "Add currency",
    currencyPlaceholder: "Enter currency name",
    currencyUpdated: "Currency has been updated.",
    currencyOptions: (c: string) => `Options for ${c}`,
    numberFormat: "Number format",
    numberFormatDesc: "Choose the preferred number format for the whole workspace.",
    currencyFormat: "Currency format",
    currencyFormatDesc: "Choose the preferred currency format for the whole workspace.",
    rates: "Rates",
    billable: "Billable hours",
    billableDesc: "Set hourly rates and overview earnings based on billable hours.",
    wsRate: "Workspace billable rate",
    wsRateDesc: "Default billable hour rate when no user or project hourly rates are set.",
    costRate: "Cost rate",
    costRateDesc: "Set hourly cost rates and overview in reports what you charge clients vs what you pay your team.",
    wsCost: "Workspace cost rate",
    wsCostDesc: "Default cost rate of each tracked hour (when a more specific cost rate isn't defined).",
    amount: "Amount",
    rateApprovedNote: "This rate will be applied to all entries, besides the approved entries.",
    taskRates: "Task rates",
    taskRatesDesc: "Set rates for each task within a project.",
    taskBillability: "Set default task billability",
    taskBillabilityDesc: "Enable setting default billability for tasks on each project.",
    expenses: "Expenses",
    expenseTracking: "Expense tracking",
    expenseTrackingDesc: "Record work-related expenses by sum or unit, add receipts, and include them in invoices.",
    expenseApproval: "Expense approval",
    expenseApprovalDesc: "Approve or reject expenses that your team submits for review.",
    expenseConfig: "Expense approval configuration",
    expenseConfigDesc: "Configure the submission frequency for your expense approvals workflow.",
    invoicing: "Invoicing",
    invoicingDesc: "Create and download invoices for clients based on tracked time.",
    address: "Company address",
    addressDesc: "The address of your company that appears on all your invoices under the “Bill from” section.",
  },

  planning: {
    timeOff: "Time off",
    timeOffDesc: "Define time off policies and holidays, request vacations, and see when people are away.",
    scheduling: "Scheduling",
    schedulingRow: "Project and team scheduling",
    schedulingDesc: (p: string) => `Plan ${p}s and visualize timelines, define team capacity and manage availability.`,
    amounts: "Show scheduled billable and cost amount",
    amountsDesc: "View amounts for each assignment based on the billable rate and cost rate.",
    amountsLocked: "Activate project and team scheduling to unlock this option.",
  },

  activity: {
    audit: "Audit log",
    auditDesc: "Track preferred user actions.",
    recordAll: "Record all changes",
    recordAllDesc: "Record all changes (create, update, and delete) made to:",
    locked: "Activate Audit log to unlock this option.",
    timeEntries: "Time entries",
    projects: "Projects",
    clients: "Clients",
    tags: "Tags",
    expenses: "Expenses",
    location: "Location tracking",
    gps: "GPS tracking",
    gpsDesc: (clients: string) =>
      `See all ${clients} job sites that were visited by your remote field workers who clock-in via the mobile app (iOS & Android).`,
    screenshots: "Screenshot capturing",
    screenshotsDesc: "Generate screenshots every 5 minutes while the timer is running (desktop app only).",
    highRes: "High-resolution screenshots",
    highResDesc:
      "Capture screenshots in higher quality for better image clarity. May increase file size and storage usage.",
    highResLocked: "Activate Screenshot capturing to unlock this option.",
  },

  kiosk: {
    config: "Kiosk configuration",
    kiosk: "Kiosk",
    kioskDesc: "Allow employees to clock in and out from designated devices.",
    switching: (p: string, t: string) => `Enable switching between ${p}s and ${t}s inside kiosk`,
    switchingDesc: "Allow employees to choose what they are working on while they're clocked in.",
    login: "Require login to launch kiosk",
    loginDesc:
      "When you open a kiosk link, choose if admin or manager first needs to log in (before employees can start clocking in).",
    locked: "Activate Kiosk to unlock this option.",
    loginOffTitle: "Turn off required login",
    loginOffBody: "Turning off required login will cause all kiosk sessions to close.",
    auth: "Authentication",
    clockIn: "Clock-in authentication",
    clockInDesc:
      "Require employees to use a dedicated PIN or a scanned QR code to verify their identity for time tracking. It can be set as required for each kiosk individually.",
    pin4: "4-digit PIN",
    pin6: "6-digit PIN",
    qr: "QR code",
    photo: "Photo capture",
    photoDesc:
      "Require photo capturing to ensure identity of the user on each clock-in and clock-out. It can be set as required for each kiosk individually.",
    consentTitle: "Privacy consent",
    consent: (f: string) => `You are about to activate ${f}.`,
    consentBold: "This feature may prove to be intrusive to the privacy of the users of your Workspace.",
    consentBody:
      "By activating it, you confirm that you have informed the users of your Workspace and obtained their consent where required by law.",
    agree: "I agree",
  },

  security: {
    card: "Two-factor authentication",
    require: "Require two-factor authentication",
    requireDesc: "Require 2FA to access workspace",
    requireHelp: "All users that don’t have 2FA enabled will be forced to enable it in order to access this workspace.",
    ownerOnly: "Only workspace owner can manage authentication settings.",
  },

  accounts: {
    userFields: "User custom fields",
    userFieldsDesc: "Add details to users for more granular reports and analysis with custom fields.",
    manage: "Manage user accounts",
    manageDesc: "Manage and audit workspace members.",
    ownerOnly: "Only workspace owner can manage user accounts.",
    search: "Search by name or email",
    email: "Email",
    registration: "Registration",
    apiKey: "API key",
    empty: "No users found",
  },

  permissions: {
    billable: "Billable hours",
    billableDesc: "Set hourly rates and see earnings based on billable hours.",
    seeRates: "Who can see hourly rates and amounts",
    changeBillable: "Who can see and change billable status of time entries and expenses",
    billableOffTitle: "Billable hours let you mark time as billable, apply hourly rates, and calculate earnings.",
    billableOff: "This permission is disabled because Billable hours isn’t activated.",
    goToSetting: "Go to setting",
    admins: "Admins",
    anyone: "Anyone",
    adminsPms: "Admins and Project managers",
    createProjects: (p: string, c: string) => `Who can create ${p}s and ${c}s`,
    createProjectsDesc: (p: string, c: string) => `Allow people to create and manage new ${p}s and ${c}s.`,
    createTasks: (t: string) => `Who can create ${t}s`,
    createTasksDesc: (t: string, p: string) => `Allow people to create new ${t}s on their ${p}s.`,
    createTags: "Who can create tags",
    createTagsDesc: "Allow people to create new tags.",
    regularSee: "Regular users can see",
    regularSeeDesc: "Prevent regular users from seeing other people’s time entries.",
    seeAll: (exp: boolean) => (exp ? "All tracked time and expenses" : "All tracked time"),
    seePublic: (exp: boolean, p: string) =>
      exp ? `Only time and expenses on public ${p}s` : `Only time on public ${p}s`,
    seeOwn: (exp: boolean) => (exp ? "Only their own time and expenses" : "Only their own time"),
    dashboard: "Users can access Team Dashboard",
    projectStatus: (p: string) => `Who can see ${p} status`,
    projectStatusDesc: (p: string) =>
      `Allow people to see status on their ${p}s so they can know how much of the estimate is left.`,
    hidePages: "Hide pages from regular users",
    hidePagesDesc: "Remove pages from sidebar so regular users can’t access them.",
    pageProjects: "Projects",
    pageTeam: "Team",
    pageReports: "Reports",
    approveTime: "Who can approve submitted timesheets",
    approveTimeDesc: "Allow managers to approve timesheets submitted by their team or project members.",
    approveExpenses: "Who can approve submitted expenses",
    approveExpensesDesc: "Allow managers to approve expenses submitted by their team or project members.",
    adminsTms: "Admins and team managers",
    adminsPmsLower: "Admins and project managers",
    editOthers: (exp: boolean) => (exp ? "Who can edit time and expenses for others" : "Who can edit time for others"),
    editOthersDesc: "Allow team managers to add/edit time for other team members.",
    invoices: "Who can manage invoices",
    invoicesDesc: "Allow people to create and edit all invoices. Refers to all projects.",
    specificMembers: "Specific members",
    assignments: "Who can create and edit assignments",
    assignmentsDesc: "Allow people to create and edit assignments for their projects.",
    seeAssignments: "Regular user can see everyone's assignments",
    kiosk: "Who can launch kiosk",
    adminsManagers: "Admins and managers",
  },

  integrations: {
    card: "Integrations",
    google: "Google Calendar",
    googleDesc: "See your events from Google Calendar in Clockify and track time on them.",
    googleBody:
      "Connect your Google Calendar by clicking on the connect icon on the Calendar page. Only you can see the events from the calendar you connected.",
    goCalendar: "Go to Calendar",
    other: "Other integrations",
    otherDesc: "Track time inside other web apps, like Asana, Trello, and 50+ others",
    otherBody: 'Install the browser extensions and you\'ll start seeing the "Start timer" button across many web apps.',
    chrome: "Get Chrome extension",
    firefox: "Get Firefox extension",
    apiKey: "Get your API key",
    seeAll: "See all integrations",
    expand: (n: string) => `Show ${n}`,
    browseMarketplace: "Browse Marketplace",
    quickbooks: "QuickBooks",
    quickbooksDesc: "Send time from Clockify to QuickBooks",
    quickbooksNote: "Note: Incompatible with QuickBooks Payroll",
    quickbooksBody:
      "QuickBooks sync needs an Intuit connection, which this workspace does not offer. Export your time from Reports and import it into QuickBooks instead.",
    learnMore: "Learn more",
    jira: "Jira",
    jiraDesc: "Track time in Clockify directly from your Jira account",
    jiraBody: "Install the Clockify app from the Atlassian Marketplace, then paste your API key into it.",
    jiraInstall: "Install Clockify for Jira",
    outlook: "Outlook Calendar",
    outlookDesc: "See your events from Microsoft Outlook Calendar in Clockify and track time on them.",
    outlookBody:
      "Connect your Outlook Calendar by clicking on the connect icon on the Calendar page. Only you can see the events from the calendar you connected.",
    addons: "Add-ons",
    noAddons: "No add-ons installed",
    noAddonsDesc: "Customize your workspace with extra tools built for your workflow.",
  },

  importExport: {
    import: "Import",
    importText1: "Follow our ",
    guidelines: "guidelines",
    importText2:
      " to prepare your file before uploading. Use the templates bellow to follow the recommended format and organize data correctly in Clockify.",
    select: "Select a CSV file to import",
    drag: "or drag and drop it here",
    max: "max. 10 MB",
    accepted: "Accepted file formats: .csv, .tsv, .ssv, .psv",
    tooBig: "Maximum file size is 10 MB",
    empty: "File is empty.",
    unreadable: "Unable to read the file. Please make sure that the formatting of the CSV file is correct.",
    mobile: "The Import feature is currently unavailable on mobile devices. Please switch to a desktop for access.",
    fileDetails: "File details",
    what: "What would you like to import?",
    chooseType: "Choose import type",
    clientsProjects: "Clients & projects",
    timesheet: "Timesheet",
    fullUsers: "Full users",
    limitedUsers: "Limited users",
    firstRow: "First row has column names",
    continue: "Continue",
    firstRowInfo: "When turned off, the first row will be imported as data",
    delimiter: "Which delimiter is used in your file?",
    delimiters: { ",": "Comma (,)", ";": "Semicolon (;)", "\t": "Tab", "|": "Pipe (|)" },
    dateFormat: "Which date format is used in your file?",
    chooseDateFormat: "Choose date format",
    timeFormat: "Which time format is used in your file?",
    h12: "12-hour",
    h24: "24-hour",
    chooseFormat: "Please choose a date & file format",
    tooManyRows: (n: number) =>
      `This file has ${n.toLocaleString("en-US")} rows. The maximum is 20,000 rows per import.`,
    revertRunning: "Revert in progress. Please wait before starting a new import.",
    steps: { mapping: "Field Mapping", inspect: "Inspect Data", summary: "Import Summary" },
    mappingText:
      "Match each CSV column with the corresponding Clockify field. Include headers in the first row of your file for easy data mapping.",
    csvColumn: "CSV COLUMN",
    matched: "MATCHED COLUMNS",
    clockifyField: "CLOCKIFY DATA FIELDS",
    selectField: "Select matching field",
    skipColumn: "Don't import",
    noMatch: "No match found. Map column to continue",
    mapRequired: (fields: string) => `Map the required Clockify fields to continue: ${fields}`,
    mapAny: "Map at least one column to continue",
    invalidRequired: (errors: string) =>
      `Invalid or missing values in required columns: ${errors}. Fix them in your CSV and reupload, or match with different columns.`,
    discardTitle: "Discard import",
    discardText: "Your changes won't be saved. Do you want to discard anyway or continue importing your data?",
    discard: "Discard",
    backToImport: "Back to import",
    back: "Back",
    inspectText: "Review your file and the data to be imported based on the first 1000 rows.",
    onlyErrors: "Only show rows with errors",
    rowCol: "Row",
    columnN: (n: number) => `Column ${n}`,
    missing: "Missing",
    missingText: "Import time only for existing users",
    seeMissing: "See missing users",
    hideMissing: "Hide missing users",
    validating: "Validating data...",
    importing: "Importing data...",
    importBtn: "Import",
    inProgress: "Import in progress (it can last up to 5 minutes). You can leave the page — your import will continue.",
    errorCount: (n: number) => (n === 1 ? "1 row has errors" : `${n} rows have errors`),
    durationMismatch: "Duration format in the file must match duration format in Workspace settings",
    errRequired: "Required",
    errDate: "Date doesn't match the chosen format",
    errTime: "Time doesn't match the chosen format",
    errEmail: "Invalid email",
    errTooLong: (max: number) => `Maximum length is ${max} characters`,
    errBillable: "Use Yes or No",
    errNumber: "Must be a number of 0 or more",
    errWeekday: "Unknown day of the week",
    errCapacity: "Must be between 0 and 24 hours",
    errTaskNeedsProject: "A task needs a project",
    fields: {
      project: "Project",
      client: "Client",
      task: "Task",
      tag: "Tag",
      email: "Email",
      startDate: "Start date",
      startTime: "Start time",
      duration: "Duration",
      description: "Description",
      billable: "Billable",
      name: "Name",
      role: "Role",
      billableRate: "Billable rate",
      costRate: "Cost rate",
      weekStart: "Week start",
      workingDays: "Working days",
      capacity: "Daily work capacity (hours)",
    },
    summaryTitle: "Your data is now imported",
    summarySub: "Review your data and get valuable insights in Clockify’s Reports.",
    summaryFailed: "Data import failed",
    goToImport: "Go to import page",
    newImport: "Start a new import",
    revertImport: "Revert import",
    counters: {
      projects: "Projects",
      clients: "Clients",
      tasks: "Tasks",
      tags: "Tags",
      timeEntries: "Time entries",
      users: "Total New Users",
      admins: "Admin",
      projectManagers: "Project Managers",
      teamManagers: "Team Managers",
      members: "Members",
      limitedUsers: "Total Limited Users",
    },
    history: "History",
    historyText: "Review and access files imported in the past 30 days. You can revert imported data within 48 hours.",
    historyEmpty: "No imports in the past 30 days",
    hDate: "Date & time",
    hBy: "Imported by",
    hType: "File type",
    hName: "File name",
    hStatus: "Status",
    hRevert: "Revert",
    revert: "Revert",
    fileTypes: {
      PROJECTS_AND_CLIENTS: "Clients & Projects",
      TIMESHEET: "Timesheet",
      FULL_USERS: "Full users",
      LIMITED_USERS: "Limited users",
    },
    statuses: {
      UPLOADING: "Import in progress",
      IMPORTING: "Import in progress",
      IMPORTED: "Imported",
      REVERTED: "Reverted",
      REVERT_IN_PROGRESS: "Revert in progress",
      FAILED_REVERT: "Revert failed",
      FAILED_IMPORT: "Failed import",
    },
    blocked: {
      EXPIRED: "Reverting is only possible in the first 48 hours.",
      USERS: "Imported users can't be reverted.",
      REVERT_RUNNING: "Please wait, another revert is in progress.",
      STATUS: "This import can't be reverted.",
    },
    revertText:
      "You will permanently delete all records, edited items, and items used elsewhere (e.g. projects, tags, tasks) from this import.",
    revertException: "Exception: You can’t delete projects that are currently in use on Kiosk.",
    revertFileName: "File name",
    revertDate: "Import date",
    revertBy: "Import by",
    typeRevert: "Type REVERT to confirm",
    revertWord: "REVERT",
    revertStarted: "Revert in progress",
    template: "Download template",
    templateDesc: "Download CSV example files for Users, Clients, Projects, or Timesheets.",
    users: "Users",
    fullUsersT: "Full Users",
    limitedUsersT: "Limited Users",
    clientsProjectsT: "Clients & Projects",
    timesheetT: "Timesheet",
    export: "Data export",
    exportDesc: "Export all time entries for a specific year.",
    year: "Year",
    exportBtn: "Export time entries",
    exporting: "Exporting data",
    ownerOnly: "Only workspace owner can export time entries.",
  },

  cf: {
    /** Workspace settings sub-pages keep the Workspaces tab title (routes.md 1.7). */
    documentTitle: "Workspaces",
    entryTitle: "Time entry custom fields",
    userTitle: "User custom fields",
    back: "Back",
    add: "Add",
    available: "Available fields",
    availableDesc: (entity: "TIMEENTRY" | "USER") =>
      entity === "USER"
        ? "Activate to add additional information to users."
        : "Activate to add additional information to time entries.",
    active: "Active fields",
    visible: "Visible",
    visibleDesc: "Fields users can see and track.",
    invisible: "Invisible",
    invisibleDesc: (entity: "TIMEENTRY" | "USER") =>
      entity === "USER" ? "Fields all users have." : "Fields all time entries have.",
    drop: "Drop here",
    emptyText:
      "Custom fields allow you to add additional information to time entries, which you can later use for filtering and reporting.",
    createEntry: "Create time entry custom field",
    createUser: "Create user custom field",
    editTitle: (n: string) => `Edit ${n}`,
    name: "Name",
    nameHelp: "Short identifier for the custom attribute.",
    nameEmpty: "Name can't be empty.",
    descriptionLabel: "Description",
    descriptionHelp: "Explanation that appears when hovering over the field.",
    type: "Type",
    chooseType: "Must choose type.",
    types: {
      TXT: "Text",
      NUMBER: "Number",
      LINK: "Link",
      CHECKBOX: "Switch",
      DROPDOWN_SINGLE: "Select",
      DROPDOWN_MULTIPLE: "Select multiple",
    },
    placeholder: "Placeholder",
    defaultValue: "Default value",
    defaultOn: "Default state ON",
    options: "Options",
    option: "Option",
    addOption: "Add option",
    removeOption: "Remove option",
    dropdownValues: "Please enter values for dropdown",
    whoCanEdit: "Who can edit",
    everyone: "Everyone",
    admins: "Admins",
    defaultShown: (v: string) => `Default: ${v}`,
    created: (n: string) => `Custom field ${n} has been created.`,
    deleted: "Custom field has been deleted.",
    deleteTitle: (n: string) => `Are you sure you want to delete ${n}?`,
    deleteBody: (n: string) =>
      `The ${n} custom field will also be removed from all time entries it is assigned to. This action cannot be reversed.`,
    typeDelete: "Type DELETE to confirm",
    deleteBtn: "Delete custom field",
    moveTo: "Move to",
    limit: "Limit reached",
  },

  profile: {
    title: "My profile",
    personal: "Personal info",
    personalDesc: "Your log-in credentials and the name that is displayed in reports.",
    name: "Name",
    email: "Email",
    nameError: "Username has to be between 1 and 100 characters long.",
    saved: "Profile updated",
    weekStart: "Week start",
    weekStartTip: "Week start is defined by the owner or the admins of the workspace.",
    password: "Change password",
    oldPassword: "Old password",
    newPassword: "New password",
    tooShort: "Password must have at least 8 characters.",
    tooLong: "Password can't be longer than 64 characters.",
    changed: "Password changed",
    incorrect: "Incorrect password",
    deleteCard: "Delete account",
    deleteLink: "Delete my account",
  },

  deleteAccount: {
    title: "Delete account",
    sub: "We're sorry to see you leave Clockify",
    rely: "There are people who rely on your workspaces:",
    before: "Before you can delete your account, you have to either:",
    transfer: "transfer your ownership role to someone else",
    orDelete: "or delete all team members from workspaces",
    done: "Once you're done, you can safely delete your account.",
    fromAll: "Your account will be deleted from all these workspaces:",
    because: "I'm deleting my account because:",
    selectReason: "Select reason",
    reasons: [
      ["Didn't find what I need", "I couldn't find how to..."],
      ["Too confusing to use", "I just need..."],
      ["Too slow or buggy", "I had trouble with..."],
      ["Using another tool", "I've decided to use..."],
    ] as const,
    button: "Delete my account",
    bullet1: "Your account and all your workspaces will be deleted forever",
    bullet2: "Time you've tracked in someone else's workspace won't be deleted",
    bullet3: "Back up your data by exporting CSV in Detailed report",
    typeDelete: "Type DELETE to confirm",
    mismatch: "Please enter the text exactly as displayed",
  },

  prefs: {
    title: "Preferences",
    tabs: { general: "GENERAL", emails: "EMAIL NOTIFICATIONS", pumble: "PUMBLE NOTIFICATIONS", advanced: "ADVANCED" },
    saved: "Preferences updated",
    themes: "Themes",
    themesDesc: "Choose your theme to suit your mood.",
    light: "Light",
    dark: "Dark",
    language: "Language",
    languageDesc: "Set language in which Clockify is displayed in all your workspaces.",
    group: "Group time entries",
    groupDesc:
      "Entries for the same activity are grouped for easier overview. Disable if you wish to see entries in chronological order.",
    groupCheck: "Group similar time entries",
    compact: (p: string) => `Compact ${p} list`,
    compactDesc: (p: string) => `Make ${p} selection easier when you have hundreds of ${p}s by collapsing the list.`,
    collapse: "Collapse if too many projects",
    threshold: "Project count threshold",
    compactHint: (n: number) => `If your clients have ${n}+ projects, all projects will be collapsed`,
    taskFilter: (t: string) => `${t} filter`,
    taskFilterDesc: (t: string, p: string) =>
      `Quickly find the right ${t} in ${p} picker by using the ${t}@${p} syntax.`,
    taskFilterCheck: (t: string) => `Activate ${t} filter`,
    timeSettings: "Time settings",
    timeSettingsDesc: "Change time zone, when your day starts, and your preferred date and time format.",
    timeZone: "Time zone",
    tzSearch: "Search time zone...",
    dateFormat: "Date format",
    timeFormat: "Time format",
    h12: "12-hour",
    h24: "24-hour",
    dayStart: "Day start",
    notifications: "Manage notifications",
    notificationsDesc: "Choose what types of email notifications you wish to receive.",
    footer:
      "Clockify may still send you messages regarding your account, required service announcements, legal notifications, and privacy matters.",
    api: "API",
    apiDesc: "Connect Clockify to other apps using your personal API key.",
    manageKeys: "Manage API keys",
    webhooks: "Webhooks",
    webhooksDesc: "Create triggers so Clockify can let you know when something happens in your workspace.",
    manageWebhooks: "Manage webhooks",
  },
  pumble: {
    title: "Manage notifications",
    desc: "Receive Clockify notifications in Pumble (by CAKE.com).",
    promo: "Send messages to teammates and talk via video meetings.",
    tryPumble: "Try Pumble",
  },
  emails: [
    ["sendNewsletter", "Newsletter", "Receive a monthly email about new features in Clockify."],
    ["onboarding", "Onboarding", "Receive an email series about key features and activities upon joining Clockify."],
    ["weeklyUpdates", "Weekly report", "Receive a weekly email about your time tracking activities."],
    ["longRunning", "Long-running timer", "Receive an email when a time entry is running more than 8 hours."],
    ["scheduledReports", "Scheduled reports", "Receive shared reports scheduled for email delivery."],
    [
      "approval",
      "Approval",
      "Receive an email when a timesheet or expense is submitted, rejected, withdrawn, or approved.",
    ],
    [
      "pto",
      "Time off",
      "Receive an email when balance is updated, or a time off request is submitted, approved, withdrawn, or rejected.",
    ],
    [
      "alerts",
      "Alerts",
      "Receive an email when a project or task reaches a certain percentage of its budget or estimated time.",
    ],
    [
      "reminders",
      "Reminders",
      "Receive an email when you or your team members miss or exceed time tracking targets, or forget to submit / approve timesheets & expenses.",
    ],
    ["scheduling", "Schedule", "Receive an email about your scheduled assignments."],
    [
      "invoiceReminders",
      "Invoices",
      "Receive an email when a recurring invoice is created or when an invoice becomes overdue.",
    ],
  ] as const,
} as const;
